import parser from 'ua-parser-js';

declare global {
    interface Window {
        DocumentTouch: any;
    }

    interface Navigator {
        msMaxTouchPoints: number;
    }
}

export interface UserAgentInterface {
    os: UAParser.IOS;
    browser: UAParser.IBrowser;
    isWindows: boolean;
    isAndroid: boolean;
    isMac: boolean;
    isIOS: boolean;
    isIPad: boolean;
    isChromebook: boolean;
    isMobile: boolean;
    deviceSupported: boolean;
    deviceType: string | undefined;
}

export const useUserAgent = (userAgent: string): UserAgentInterface => {
    const ua = new parser.UAParser(userAgent);

    const matchOs = (regex: RegExp): boolean => {
        const os = ua.getOS().name;
        if (!os) return false;
        return regex.test(os);
    };

    const isTouchDevice = (): boolean => (
        !!(typeof window !== 'undefined' &&
            ('ontouchstart' in window ||
                (window.DocumentTouch &&
                    typeof document !== 'undefined' &&
                    document instanceof window.DocumentTouch))) ||
        !!(typeof navigator !== 'undefined' &&
            (navigator.maxTouchPoints || navigator.msMaxTouchPoints))
    );

    const isWindows = (): boolean => {
        const regex = new RegExp('Windows', 'ig');
        return matchOs(regex);
    };

    const isMac = (): boolean => {
        const regex = new RegExp('Mac', 'ig');
        return matchOs(regex) && !isTouchDevice();
    };

    const isAndroid = (): boolean => {
        const regex = new RegExp('Android', 'ig');
        return matchOs(regex);
    };

    const isChromebook = (): boolean => {
        const regex = new RegExp('Chromium OS', 'ig');
        return matchOs(regex);
    };

    const isIPad = (): boolean => {
        const regex = new RegExp('Mac', 'ig');
        return matchOs(regex) && isTouchDevice();
    };

    const isIOS = (): boolean => {
        const regex = new RegExp('iOS', 'ig');
        return matchOs(regex);
    };

    const deviceSupported = (): boolean => {
        // Note that 'desktop' is not a type that ua-parser-js can detect, but I've put it in here just in case they
        // add it as a device type in the future.
        const supported = ['desktop', 'tablet'];
        const deviceType = ua.getDevice().type;
        if (!deviceType) {
            // Assume supported if device type not detected (this is how desktop functions at time of coding).
            return true;
        }
        return supported.includes(deviceType);
    };

    return {
        os: ua.getOS(),
        browser: ua.getBrowser(),
        isWindows: isWindows(),
        isAndroid: isAndroid(),
        isMac: isMac(),
        isIOS: isIOS(),
        isIPad: isIPad(),
        isChromebook: isChromebook(),
        isMobile: isIOS() || isAndroid() || isChromebook(),
        deviceSupported: deviceSupported(),
        deviceType: ua.getDevice().type
    };
};
